import React from 'react';
import {
  Button,
  Card,
  EmptyState,
  Flex,
  LoadingOrbit,
  Page,
  PageHeader,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
  useToast,
} from '@chatsdet/ui';
import { Cube, FileText, LinkSimple, Trash } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  getUtilityObjectUrl,
  useDeleteUtilityGist,
  useDeleteUtilityObject,
  useUtilityGists,
  useUtilityObjects,
  type UtilityObjectItem,
} from '../../features/utilities/api';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Gists tab ────────────────────────────────────────────────────────────────

function GistsTab() {
  const { data, isLoading } = useUtilityGists();
  const del = useDeleteUtilityGist();
  const { toast } = useToast();
  const navigate = useNavigate();

  const gists = data?.data ?? [];
  if (isLoading && gists.length === 0) return <LoadingOrbit />;
  if (gists.length === 0) {
    return (
      <EmptyState
        icon={<FileText size={32} />}
        title="No gists yet"
        description="Create one from the CLI: cofactor utilities gist create report.md --public"
      />
    );
  }

  return (
    <Flex direction="column" gap="sm">
      {gists.map((g) => (
        <Card key={g.id} className="p-3">
          <Flex align="center" justify="between">
            <button
              className="text-left"
              onClick={() =>
                void navigate({ to: '/utilities/gists/$id', params: { id: g.id } })
              }
            >
              <Text weight="medium">{g.title}</Text>
              <Text className="text-muted-foreground text-xs">
                {g.visibility} · {g.format} · {new Date(g.createdAt).toLocaleString()}
              </Text>
            </button>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                del.mutate(g.id, {
                  onSuccess: () => toast.success('Gist deleted'),
                  onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed'),
                });
              }}
            >
              <Trash size={16} />
            </Button>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
}

// ── Objects tab ─────────────────────────────────────────────────────────────

function ObjectRow({ o }: { o: UtilityObjectItem }) {
  const del = useDeleteUtilityObject();
  const { toast } = useToast();

  const copyLink = async () => {
    try {
      const url = o.publicUrl ?? (await getUtilityObjectUrl(o.id));
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to get link');
    }
  };

  return (
    <Card className="p-3">
      <Flex align="center" justify="between">
        <div>
          <Text weight="medium">{o.filename}</Text>
          <Text className="text-muted-foreground text-xs">
            {o.visibility} · {o.contentType} · {formatSize(o.sizeBytes)}
            {o.expiresAt ? ` · expires ${new Date(o.expiresAt).toLocaleDateString()}` : ''}
          </Text>
        </div>
        <Flex align="center" gap="xs">
          <Button variant="ghost" size="sm" onPress={copyLink}>
            <LinkSimple size={16} /> {o.publicUrl ? 'Copy link' : 'Get link'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onPress={() =>
              del.mutate(o.id, {
                onSuccess: () => toast.success('Object deleted'),
                onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed'),
              })
            }
          >
            <Trash size={16} />
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

function ObjectsTab() {
  const { data, isLoading } = useUtilityObjects();
  const objects = data?.data ?? [];

  if (isLoading && objects.length === 0) return <LoadingOrbit />;
  if (objects.length === 0) {
    return (
      <EmptyState
        icon={<Cube size={32} />}
        title="No objects yet"
        description="Upload one from the CLI: cofactor utilities object upload chart.png --public"
      />
    );
  }

  return (
    <Flex direction="column" gap="sm">
      {objects.map((o) => (
        <ObjectRow key={o.id} o={o} />
      ))}
    </Flex>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export function Utilities() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Utilities is not GA — bounce out if the org's flag is off.
  React.useEffect(() => {
    if (user && !user.features?.utilities) {
      void navigate({ to: '/' });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    setBreadcrumbs([{ label: 'Utilities' }]);
  }, [setBreadcrumbs]);

  const [tab, setTab] = React.useState<'gists' | 'objects'>('gists');

  return (
    <Page>
      <PageHeader
        title="Utilities"
        description="Agent toolkit — text/markdown gists and uploaded objects. Manage from the CLI: cofactor utilities"
      />
      <Tabs selectedKey={tab} onSelectionChange={(k) => setTab(k as 'gists' | 'objects')}>
        <TabList aria-label="Utilities views">
          <Tab id="gists">Gists</Tab>
          <Tab id="objects">Objects</Tab>
        </TabList>
        <TabPanel id="gists">
          <GistsTab />
        </TabPanel>
        <TabPanel id="objects">
          <ObjectsTab />
        </TabPanel>
      </Tabs>
    </Page>
  );
}
